import { CustomerProductName } from 'src/constants/customer/enums'
import type { CustomerProduct } from 'src/interfaces/customer'
import type { GigsafeConfig } from 'src/interfaces/gigsafeConfig'

/**
 * Matches a string built only from empty-document markup: paragraph/div wrappers, line breaks,
 * non-breaking spaces and whitespace. `react-simple-wysiwyg` leaves one of these behind when
 * the admin clears a field, and persisting it renders a stray blank paragraph in the
 * contractor-facing app. Retool's `cleanPartnerGigsafeConfig` did the same for Quill's output.
 */
const EMPTY_RICH_TEXT_PATTERN = /^(?:<p>|<\/p>|<div>|<\/div>|<br\s*\/?>|&nbsp;|\s)*$/i

export function isEmptyRichText(html: string | null | undefined): boolean {
  if (html === null || html === undefined) {
    return true
  }

  return EMPTY_RICH_TEXT_PATTERN.test(html)
}

export function blankToNull(value: string | null | undefined): string | null {
  if (value === null || value === undefined) {
    return null
  }

  const trimmed = value.trim()

  if (trimmed === '') {
    return null
  }

  return trimmed
}

/**
 * `GET /gigsafe-config` resolves `null` when a partner has no config row yet — the API returns
 * a 200 with an empty body rather than a 404. The fetch saga therefore requests the body as
 * text (`returnResponse: true`) and parses it here, so the brand-new-customer case cannot
 * surface as a `response.json()` SyntaxError.
 *
 * Malformed JSON on a 200 is a genuine anomaly and throws, so the saga's `captureSentryError`
 * records it instead of quietly reporting "no config".
 */
export function parseGigsafeConfigResponse(raw: unknown): GigsafeConfig | null {
  if (raw === null || raw === undefined) {
    return null
  }

  if (typeof raw === 'object') {
    if (Array.isArray(raw)) {
      return null
    }

    return raw as GigsafeConfig
  }

  if (typeof raw !== 'string') {
    return null
  }

  const trimmed = raw.trim()

  if (trimmed === '' || trimmed === 'null') {
    return null
  }

  let parsed: unknown

  try {
    parsed = JSON.parse(trimmed)
  } catch {
    throw new Error('Malformed gigsafe config response')
  }

  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null
  }

  return parsed as GigsafeConfig
}

/**
 * Notion §6.4 documents wire transfers as required when the customer runs Payments and no
 * transfer route is enabled. Rendered as a warning, never a validation error: rows written by
 * Retool may already sit in this state, and blocking Save would strand unrelated edits.
 */
export function isWireTransferRequired(
  products: CustomerProduct[] | undefined,
  transfers: { loanTransferEnabled: boolean; wireTransferEnabled: boolean },
): boolean {
  if (!products) {
    return false
  }

  const hasPaymentsProduct = products.some((product) => {
    return product.product === CustomerProductName.Payments
  })

  if (!hasPaymentsProduct) {
    return false
  }

  return !transfers.loanTransferEnabled && !transfers.wireTransferEnabled
}
